/* eslint-disable no-console */
import { fromJS as _fromJS, List, Map as ImmutableMap, OrderedMap, Record as ImmutableRecord, Set as ImmutableSet, Iterable } from 'immutable';
import fromJSOrdered from './fromJSOrdered';
import * as overrides from './overrides';

/**
 * Utility type that flattens complex type intersections into unified object types.
 *
 * This forces TypeScript to display intersections as unified objects in error messages
 * by using conditional type inference to resolve intersection syntax into final form.
 *
 * @template T - The intersection type to flatten
 * @example
 * ```typescript
 * // Instead of: { a: string } & { b: number }
 * // Shows: { a: string; b: number }
 * type Clean = FlattenIntersection<{ a: string } & { b: number }>;
 * ```
 */

/**
 * Semantic wrapper type that prevents massive inline type expansions.
 *
 * This type alias wraps ImmutableJS RecordOf types to show "CheckedRecord<T>"
 * in error messages instead of the full expanded RecordOf structure with all
 * its methods (has, get, set, etc.).
 *
 * @template T - The object type to wrap as a CheckedRecord
 * @example
 * ```typescript
 * // Shows as: CheckedRecord<{ name: string; age: number }>
 * // Instead of: RecordOf<{ name: string; age: List<...> }> & { has: ..., get: ..., set: ... }
 * type PersonRecord = CheckedRecord<{ name: string; age: number }>;
 * ```
 */
/**
 * Helper type for validating compatibility with array-like structures (Arrays and ImmutableJS Lists)
 */
/**
 * Helper type for validating compatibility with Map-like structures (ES6 Maps and ImmutableJS Maps)
 */
/**
 * Helper type for validating compatibility with generic Record types to ImmutableMap
 */
/**
 * Helper type for validating compatibility with specific object types
 */
/**
 * Recursively validates TypeScript types for compatibility with ImmutableJS checked structures.
 *
 * This is the core compatibility validation engine that ensures regular TypeScript types
 * are compatible with their ImmutableJS equivalents while preserving type safety.
 * It handles arrays, objects, maps, and functions appropriately.
 *
 * @template T - The type to recursively validate compatibility for
 *
 * @example
 * ```typescript
 * // Input: { users: User[], settings: Map<string, any> }
 * // Output: { users: List<User>, settings: ImmutableMap<string, any> } (validated structure)
 * type CompatibleType = RecursiveCheckedCompatibleWith<{ users: User[], settings: Map<string, any> }>;
 * ```
 *
 * **Compatibility Rules:**
 * - `Array<T> | List<T>` → `List<RecursiveCheckedCompatibleWith<T>>` (CompatibleArrayLike)
 * - `Map<K, V> | ImmutableMap<K, V>` → `ImmutableMap<K, RecursiveCheckedCompatibleWith<V>>` (CompatibleMapLike)
 * - `Record<string, V>` → `ImmutableMap<string, RecursiveCheckedCompatibleWith<V>>` (CompatibleRecordLike)
 * - Specific objects → `RecordOf<{ [K in keyof T]: RecursiveCheckedCompatibleWith<T[K]> }>` (CompatibleObjectLike)
 * - Functions, primitives → `T` (unchanged)
 */
/**
 * Main export type for validating checked record compatibility.
 *
 * This creates a ChainedConstructor that validates plain JavaScript objects
 * are compatible with ImmutableJS Records with recursive type validation. The resulting
 * validator includes validation methods (optional, required, validate, etc.) and
 * ensures type compatibility for immutable data structures.
 *
 * @template T - The object type to validate compatibility for (must extend object)
 *
 * @example
 * ```typescript
 * interface User {
 *   name: string;
 *   emails: string[];
 *   preferences: { theme: string; notifications: boolean };
 * }
 *
 * const UserRecord = record({
 *   name: string(),
 *   emails: list(string()),
 *   preferences: record({
 *     theme: string(),
 *     notifications: boolean()
 *   })
 * }) satisfies CheckedRecordCompatibleWith<User>;
 *
 * // Validates compatibility and creates immutable structure
 * const user = UserRecord({
 *   name: "John",
 *   emails: ["john@example.com"],
 *   preferences: { theme: "dark", notifications: true }
 * });
 * ```
 */
/**
 * Validator for polymorphic checked types with a discriminant field.
 *
 * This is used for validating union types that have a common
 * discriminant property (like a "type" field). It validates the discriminant
 * property exists and ensures compatibility with the appropriate immutable type.
 *
 * @template T - The union type with a discriminant property
 *
 * @example
 * ```typescript
 * type Shape =
 *   | { type: "circle"; radius: number }
 *   | { type: "square"; side: number };
 *
 * const ShapeRecord = poly('type', {
 *   circle: record({ type: symbol({ circle: 'circle' }), radius: number() }),
 *   square: record({ type: symbol({ square: 'square' }), side: number() })
 * }) satisfies CheckedPolyCompatibleWith<Shape>;
 *
 * // Validates compatibility and creates immutable structure
 * const circle = ShapeRecord({ type: "circle", radius: 5 });
 * ```
 */
/**
 * Validator for symbol/enum types with constrained string values.
 *
 * This validates that a string value is compatible with one of the allowed
 * symbol values defined in the constraint object.
 *
 * @template T - Object type mapping string keys to string values
 *
 * @example
 * ```typescript
 * const Colors = { RED: 'red', BLUE: 'blue', GREEN: 'green' } as const;
 *
 * const ColorValidator = symbol(Colors) satisfies CheckedSymbolCompatibleWith<typeof Colors>;
 *
 * // Validates compatibility with allowed color values
 * const validColor = ColorValidator('red'); // ✓ Valid
 * ```
 */
const isLoggingEnabled = () => {
  try {
    return overrides.checkedRecordLogging.enabled;
  } catch (e) {
    return false;
  }
};
const logger = {
  warn: (stack, title, ...messages) => {
    if (!isLoggingEnabled()) {
      return;
    }
    console.groupCollapsed(`Warning: ${title}`);
    console.warn(); // for test suite
    messages.forEach(message => typeof message === 'function' ? message() : console.log(message));
    console.log(`%c${stack}`, 'color: red');
    console.groupEnd();
  },
  error: (stack, title, ...messages) => {
    if (!isLoggingEnabled()) {
      return;
    }
    console.groupCollapsed(`Error: ${title}`);
    console.error(); // for test suite
    messages.forEach(message => typeof message === 'function' ? message() : console.log(message));
    console.log(`%c${stack}`, 'color: red');
    console.groupEnd();
  }
};
const captureStackTrace = () => {
  if (!isLoggingEnabled()) {
    return '';
  }
  return new Error('').stack || 'Stack only available in certain browsers';
};
const VALIDATE = 'validate';
const ANY = 'any';
const UNKNOWN = 'unknown';
const BOOLEAN = 'boolean';
const STRING = 'string';
const NUMBER = 'number';
const SYMBOL = 'symbol';
const FUNC = 'func';
const LIST = 'List';
const ITERABLE = 'Iterable';
const MAP = 'Map';
const RECORD = 'Record';
const POLY = 'Poly';
const type = (describe, evaluate) => {
  evaluate.describe = describe;
  return evaluate;
};
const identity = any => any;
const result = (expression, ...args) => typeof expression === 'function' ? expression(...args) : expression;
const always = (expression = identity, constant) => type(() => Object.assign({}, expression.describe(), {
  always: constant
}), (value, {
  origin = value,
  parent,
  path,
  stack = captureStackTrace()
} = {}) => {
  if (value !== constant) {
    logger.warn(stack, `Mismatching value provided to always constant {${path}}`, 'Name: always', `Path: ${path}`, 'Message: Expected constant value', () => console.log('Expected:', constant), () => console.log('Got:', value), () => console.log('Origin:', origin));
  }
  return result(expression, constant, {
    origin,
    parent,
    path,
    stack
  });
});
const required = (expression = identity) => type(() => {
  const description = expression.describe();
  delete description.optional;
  return description;
}, (value, {
  origin = value,
  parent,
  path,
  stack = captureStackTrace()
} = {}) => {
  if (value === undefined) {
    logger.warn(stack, `Missing required value at {${path}}`, 'Name: required', `Path: ${path}`, 'Message: Expected defined value', () => console.log('Value:', value), () => console.log('Origin:', origin));
  }
  return result(expression, value, {
    origin,
    parent,
    path,
    stack
  });
});
const optional = (expression = identity) => type(() => Object.assign({}, expression.describe(), {
  optional: true
}), (value, ...rest) => value === undefined ? value : result(expression, value, ...rest));
const defaultValue = (expression = identity, fallback) => type(() => Object.assign({}, expression.describe(), fallback ? {
  defaultValue: fallback
} : {}), (maybeValue = fallback, ...rest) => result(expression, maybeValue, ...rest));
const fromJS = (expression = identity) => (value, ...rest) => result(expression, _fromJS(value), ...rest);
const _fromJSOrdered = (expression = identity) => (value, ...rest) => result(expression, fromJSOrdered(value), ...rest);
const validate = (expression = identity, name = VALIDATE, message = '') => (value, {
  origin = value,
  parent,
  path = name,
  stack = captureStackTrace()
} = {}) => {
  try {
    if (!result(expression, value, {
      origin,
      parent,
      path,
      stack
    })) {
      logger.warn(stack, `Invalid value at {${path}}`, `Name: ${name}`, `Path: ${path}`, typeof message === 'function' ? message : `Message: ${message}`, () => console.log('Value:', value), () => console.log('Origin:', origin));
    }
    return value;
  } catch (validationError) {
    logger.error(stack, `Errored value at {${path}}`, `Name: ${name}`, `Path: ${path}`, typeof message === 'function' ? message : `Message: ${message}`, () => console.log('Value:', value), () => console.log('Origin:', origin), () => console.log('Validation Error:', validationError));
    throw validationError;
  }
};
const chainable = (fn, name) => {
  const pipe = next => (...args) => {
    const expression = next(...args);
    return type(expression.describe || fn.describe, chainable((value, ...rest) => expression(fn(value, ...rest), ...rest)));
  };
  const decorate = decorator => (...decorateArgs) => {
    const expression = decorator(fn, ...decorateArgs);
    return type(expression.describe || fn.describe, chainable((value, {
      origin = value,
      parent,
      path = name,
      stack = captureStackTrace()
    } = {}) => expression(value, {
      origin,
      parent,
      path,
      stack
    })));
  };
  const chainedFn = fn;
  chainedFn.validate = pipe(validate);
  chainedFn.always = decorate(always);
  chainedFn.required = decorate(required);
  chainedFn.optional = decorate(optional);
  chainedFn.defaultValue = decorate(defaultValue);
  chainedFn.fromJS = decorate(fromJS);
  chainedFn.fromJSOrdered = decorate(_fromJSOrdered);
  /* eslint-enable no-use-before-define */
  return chainedFn;
};
export const any = (name = ANY) => type(() => ({
  type: ANY,
  name,
  label: name
}), chainable(validate(true, name), name));
export const unknown = (name = UNKNOWN) => type(() => ({
  type: UNKNOWN,
  name,
  label: name
}), chainable(validate(true, name), name));
export const boolean = (name = BOOLEAN) => type(() => ({
  type: BOOLEAN,
  name,
  label: name
}), chainable(validate(value => typeof value === typeof true, name, 'Expected a boolean'), name).required());
export const number = (name = NUMBER) => type(() => ({
  type: NUMBER,
  name,
  label: name
}), chainable(validate(value => typeof value === 'number', name, 'Expected a number'), name).required());
export const string = (name = STRING) => type(() => ({
  type: STRING,
  name,
  label: name
}), chainable(validate(value => typeof value === 'string', name, 'Expected a string'), name).required());
export const symbol = (object, name = SYMBOL) => {
  const set = ImmutableSet(_fromJS(object).toList());
  return type(() => ({
    type: SYMBOL,
    name,
    label: name,
    set
  }), chainable(validate(value => set.contains(value), name, () => console.log('Message: Expected one of', set.toJS())), name).required());
};
export const func = (name = FUNC) => type(() => ({
  type: FUNC,
  name,
  label: name
}), chainable(validate(value => typeof value === 'function', name, 'Expected a function'), name).required());
export const list = (expression = any(), name = LIST) => type(() => {
  const valueType = expression.describe();
  return {
    type: LIST,
    name,
    label: `List<${valueType.label}>`,
    value: valueType
  };
}, chainable((values, {
  origin = values,
  parent,
  path = name,
  stack = captureStackTrace()
} = {}) => {
  try {
    return List(validate(arr => {
      if (Array.isArray(arr) || List.isList(arr)) {
        return true;
      }

      /* Validate will log it as an errored value & rethrow this error */
      throw new Error();
    }, name, 'Expected an Array or List')(values, {
      origin,
      parent,
      path,
      stack
    })).map((value, key) => result(expression, value, {
      origin,
      parent: values,
      path: `${path}[${key}]`,
      stack
    }));
  } catch (e) {
    /* Lets pass the value through, we already logged any possible errors */
    return values;
  }
}, name).required());
export const listOrIterableToList = (expression = any(), name = ITERABLE) => type(() => {
  const valueType = expression.describe();
  return {
    type: LIST,
    name,
    label: `List<${valueType.label}>`,
    value: valueType
  };
}, chainable((values, {
  origin = values,
  parent,
  path = name,
  stack = captureStackTrace()
} = {}) => {
  try {
    return List(validate(arr => {
      if (Array.isArray(arr) || Iterable.isIterable(arr)) {
        return true;
      }

      /* Validate will log it as an errored value & rethrow this error */
      throw new Error();
    }, name, 'Expected an Array or Iterable')(values, {
      origin,
      parent,
      path,
      stack
    })).map((value, key) => result(expression, value, {
      origin,
      parent: values,
      path: `${path}[${key}]`,
      stack
    }));
  } catch (e) {
    /* Lets pass the value through, we already logged any possible errors */
    return values;
  }
}, name).required());
export const kvmap = (keyExpression = any(), valueExpression = any(), name = MAP) => type(() => {
  const keyType = keyExpression.describe();
  const valueType = valueExpression.describe();
  return {
    type: MAP,
    name,
    label: `Map<${keyType.label}, ${valueType.label}>`,
    key: keyType,
    value: valueType
  };
}, chainable((object, {
  origin = object,
  parent,
  path = name,
  stack = captureStackTrace()
} = {}) => {
  try {
    return ImmutableMap(validate(obj => {
      if (obj === Object(obj) || ImmutableMap.isMap(obj)) {
        return true;
      }

      /* Validate will log it as an errored value & rethrow this error */
      throw new Error();
    }, name, 'Expected an Object or Map')(object, {
      origin,
      parent,
      path,
      stack
    })).mapKeys(key => result(keyExpression, key, {
      origin,
      parent: object,
      path: `${path}@${key}`,
      stack
    })).map((value, key) => result(valueExpression, value, {
      origin,
      parent: object,
      path: `${path}[${key}]`,
      stack
    }));
  } catch (e) {
    /* Lets pass the value through, we already logged any possible errors */
    return object;
  }
}, name).required());
export const map = (valueExpression = any(), name = MAP) => kvmap(string(), valueExpression, name);
export const record = (expressions, name = RECORD) => {
  const expressionMap = ImmutableMap(expressions);
  const defaultValues = expressionMap.map(() => undefined).toObject();
  const factory = ImmutableRecord(defaultValues, name);
  return type(() => ({
    type: RECORD,
    name,
    label: name,
    fields: OrderedMap(expressions).map((expression = any()) => expression.describe())
  }), chainable((object, {
    origin = object,
    parent,
    path = name,
    stack = captureStackTrace()
  } = {}) => {
    const values = ImmutableMap(validate(obj => ImmutableMap(obj), name, 'Expected an Object or Map')(object, {
      origin,
      parent,
      path,
      stack
    }));
    values.filter((_, key) => !expressionMap.has(key)).forEach((value, key) => {
      logger.warn(stack, `Unexpected key at {${path}}`, `Name: ${name}`, `Path: ${path}`, 'Message: Unexpected key for checked record', () => console.log('Key:', key), () => console.log('Value:', value), () => console.log('Origin:', origin));
    });
    return factory(expressionMap.map((expression = identity, key) => result(expression, values.get(key), {
      origin,
      parent: object,
      path: `${path}.${key}`,
      stack
    })));
  }, name).required());
};
export const recursive = producer => {
  const recursiveExpression = type(() => ({
    recursive: true
  }), chainable((...args) => recursive(producer)(...args)).optional());
  const rootExpression = producer(recursiveExpression);
  const {
    type: rootType,
    name: rootName,
    label: rootLabel
  } = rootExpression.describe();
  const recursiveDescription = Object.assign({
    type: rootType,
    name: rootName,
    label: rootLabel
  }, recursiveExpression.describe());
  recursiveExpression.describe = () => recursiveDescription;
  return rootExpression;
};
export function poly(field, types, name = POLY) {
  return type(() => ({
    type: POLY,
    name,
    label: name,
    field,
    types: OrderedMap(types).map((expression = any()) => expression.describe())
  }), chainable((object, {
    origin = object,
    parent,
    path,
    stack = captureStackTrace()
  } = {}) => {
    try {
      const typeName = ImmutableMap.isMap(object) ? object.get(field) : object[field];
      const typeConstructor = types[typeName];
      return typeConstructor(object, {
        origin,
        parent,
        path,
        stack
      });
    } catch (e) {
      try {
        validate(() => {
          throw e;
        }, name, 'Failed to construct poly type')(object, {
          origin,
          parent,
          path,
          stack
        });

        // eslint-disable-next-line no-empty
      } catch (ignored) {
        // Intentionally empty - validation already logged the error
      }
    }
    return object;
  }).required());
}